import { ref, reactive } from 'vue'

interface UserStats {
  points: number
  pointsGiven: number
  redeemablePoints: number
  rank: number | null
}

interface StatsState {
  stats: UserStats
  loading: boolean
  error: string | null
  lastFetched: number | null
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000

const state = reactive<StatsState>({
  stats: {
    points: 0,
    pointsGiven: 0,
    redeemablePoints: 0,
    rank: null
  },
  loading: false,
  error: null,
  lastFetched: null
})

export function useStatsStore() {
  const isStale = () => {
    if (!state.lastFetched) return true
    return Date.now() - state.lastFetched > CACHE_DURATION
  }

  const fetchStats = async (force = false) => {
    // Don't fetch if we have recent data and not forcing refresh
    if (!force && !isStale() && !state.loading) {
      return state.stats
    }

    // Don't fetch if already loading
    if (state.loading) {
      return state.stats
    }

    state.loading = true
    state.error = null

    try {
      const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/my-stats`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch stats')
      }

      const data = await response.json()
      
      state.stats = {
        points: data.points || 0,
        pointsGiven: data.pointsGiven || 0,
        redeemablePoints: data.redeemablePoints || 0,
        rank: data.rank || null
      }
      
      state.lastFetched = Date.now()
      return state.stats
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Failed to fetch stats'
      console.error('Error fetching user stats:', error)
      throw error
    } finally {
      state.loading = false
    }
  }

  const refreshStats = () => {
    return fetchStats(true)
  }

  const getStats = () => {
    return state.stats
  }

  const getLoading = () => {
    return state.loading
  }

  const getError = () => {
    return state.error
  }

  return {
    stats: state.stats,
    loading: state.loading,
    error: state.error,
    fetchStats,
    refreshStats,
    getStats,
    getLoading,
    getError
  }
} 